import { Product } from "../../common/models/product";
import {
  canonicalizePriceUnit,
  convertDimensionUnits,
  isBarWeightRemnant,
  isLengthUnit,
  isUnitUnit,
  isWeightUnit,
  shapeIsBarTube,
  shapeIsPowder,
  shapeIsSheet,
} from "./helpers";
import { convertPriceValueAndUnit } from "./price-helpers";

const UNIVERSAL_QUANTITY_UNITS = ["ft", "in", "m", "mm", "unit"] as const;
const WEIGHT_QUANTITY_UNITS = ["lb", "kg"] as const;

export const simpleFormatUnit = (unit: string) => {
  if (unit === "unit") {
    return "units";
  }
  if (unit === "sqft") {
    return "sq ft";
  }
  if (unit === "sqin") {
    return "sq in";
  }
  if (unit === "sqm") {
    return "m²";
  }
  return unit;
};

const MINIMUM_OBTAINABLE_LENGTH_FT = 3;

const formatMinimumLengthForUnit = (value: number, unit: string) => {
  const decimalPlaces = unit === "m" ? 2 : 0;
  return value.toFixed(decimalPlaces);
};

const getMinimumLengthError = ({
  product,
  parsedQuantity,
  quantityUnit,
}: {
  product: Product;
  parsedQuantity: number;
  quantityUnit?: string;
}) => {
  if (!quantityUnit || !isLengthUnit(quantityUnit)) {
    return null;
  }

  // The 3 ft cut minimum only applies to material sold cut-to-length (bars,
  // tubes). Sheets, plates, weight-stocked bar remnants, and piece-stocked items
  // are sold as whole pieces, so a length-based minimum would be meaningless.
  if (!shapeIsBarTube(product.shape || "") || isBarWeightRemnant(product)) {
    return null;
  }

  const requestedLengthUnit: string = quantityUnit;
  const minimumComparisonTolerance = 1e-6;

  const productLength = product.standardDimensions?.length?.hi;
  if (!productLength) {
    return null;
  }

  const productLengthInRequestedUnit = convertDimensionUnits(
    productLength.unit,
    requestedLengthUnit,
    productLength.value,
  );
  const minimumObtainableLengthInRequestedUnit = convertDimensionUnits(
    "ft",
    requestedLengthUnit,
    MINIMUM_OBTAINABLE_LENGTH_FT,
  );

  if (
    parsedQuantity < productLengthInRequestedUnit &&
    parsedQuantity <
      minimumObtainableLengthInRequestedUnit - minimumComparisonTolerance
  ) {
    if (requestedLengthUnit !== "ft") {
      return `Minimum quantity: 3 feet (${formatMinimumLengthForUnit(
        minimumObtainableLengthInRequestedUnit,
        requestedLengthUnit,
      )} ${requestedLengthUnit})`;
    }
    return "Minimum quantity: 3 feet";
  }

  return null;
};

const formatMinPieces = (value: number, unit: string) => {
  const decimalPlaces = unit === "m" || unit === "kg" ? 2 : 0;
  const rounded =
    Math.round(value * Math.pow(10, decimalPlaces)) /
    Math.pow(10, decimalPlaces);
  return rounded.toFixed(decimalPlaces);
};

const getVendorMinimumQuantityError = ({
  product,
  parsedQuantity,
  quantityUnit,
}: {
  product: Product;
  parsedQuantity: number;
  quantityUnit?: string;
}) => {
  const min = product.minimumOrderQuantity;
  if (typeof min !== "number" || !Number.isFinite(min) || min <= 0) {
    return null;
  }

  const unit = quantityUnit || "unit";
  if (isUnitUnit(unit)) {
    if (parsedQuantity + 1e-6 < min) {
      return `Minimum quantity: ${min}`;
    }
    return null;
  }

  if (isLengthUnit(unit)) {
    const stock = product.standardDimensions?.length?.hi;
    if (!stock || typeof stock.value !== "number") {
      return parsedQuantity + 1e-6 < min ? `Minimum quantity: ${min}` : null;
    }
    const stockInUnit = convertDimensionUnits(stock.unit, unit, stock.value);
    if (!stockInUnit) return null;
    const pieces = parsedQuantity / stockInUnit;
    if (pieces + 1e-6 < min) {
      return `Minimum quantity: ${min} (${formatMinPieces(
        min * stockInUnit,
        unit,
      )} ${unit})`;
    }
    return null;
  }

  return null;
};

export const getQuantityError = ({
  product,
  quantityRequired,
  quantityUnit,
}: {
  product: Product;
  quantityRequired: string;
  quantityUnit?: string;
}) => {
  const parsedQuantity = parseFloat(quantityRequired);

  if (
    quantityRequired.trim() !== "" &&
    (Number.isNaN(parsedQuantity) || parsedQuantity <= 0)
  ) {
    return "Please enter a valid quantity";
  }

  if (quantityRequired.trim() !== "") {
    const minimumLengthError = getMinimumLengthError({
      product,
      parsedQuantity,
      quantityUnit,
    });
    if (minimumLengthError) {
      return minimumLengthError;
    }
    const vendorMinimumError = getVendorMinimumQuantityError({
      product,
      parsedQuantity,
      quantityUnit,
    });
    if (vendorMinimumError) {
      return vendorMinimumError;
    }
  }

  // No cap against on-hand stock: buyers order the quantity they need and we
  // source whatever isn't on the shelf, so the only limits left are physical
  // ones (a positive number, and the cut minimum for bars and tubes).
  return null;
};

/**
 * Map a stored quantity unit onto one the cart dropdown actually offers.
 * Quotes and RFQs save "pieces"; the cart's piece choice is "unit" (shown as
 * "units"). Without this, a bar quoted as 1 piece is treated as an unknown
 * unit and the dropdown snaps to ft — which is what confused the customer.
 */
export const resolveQuantityUnit = (
  requested: string | undefined,
  available: string[],
): string | undefined => {
  if (available.length === 0) return undefined;
  if (requested && available.includes(requested)) return requested;
  const canonical = canonicalizePriceUnit(requested || "");
  if (canonical && available.includes(canonical)) return canonical;
  return available[0];
};

export const getAvailableQuantityUnits = ({
  product,
  preferredUnit,
}: {
  product: Product;
  preferredUnit?: string;
}) => {
  const shape = product.shape || "";

  // Powder has no length or piece geometry: it is bought by weight alone.
  // Offering "ft of powder" would only ever show "price on request".
  let units: string[];
  if (shapeIsPowder(shape)) {
    units = [...WEIGHT_QUANTITY_UNITS];
  } else if (shapeIsSheet(shape) || isBarWeightRemnant(product)) {
    // Sheets/plates and weight-stocked bar remnants are sold as whole pieces.
    // Weight is informational only; offer "piece" and derive the per-piece
    // price from the actual on-hand weight (not nominal mill-bar geometry).
    units = ["unit"];
    // Catalog sheets quoted by coverage ("10 SF" on an RFQ) can also be
    // ordered by square foot when the price spreads over a known face area.
    // Offered, never imposed — the default stays per piece. Weight-stocked
    // remnants stay piece-only: a remnant is one specific piece, not coverage
    // you can order 10 sq ft of.
    if (
      shapeIsSheet(shape) &&
      !isWeightUnit(product.quantity?.unit) &&
      product.price?.value &&
      convertPriceValueAndUnit(product, "sqft")
    ) {
      units = [...units, "sqft"];
    }
  } else {
    units = [...UNIVERSAL_QUANTITY_UNITS];

    // A product priced by weight whose geometry we cannot compute (no density
    // or dimensions) can't be priced in any of the units above. Put weight
    // units first so the default unit is one that actually shows the price;
    // the others stay selectable for quantity-by-piece/length requests.
    const priceUnit = canonicalizePriceUnit(product.price?.unit || "");
    if (
      product.price?.value &&
      priceUnit &&
      isWeightUnit(priceUnit) &&
      !units.some((unit) => convertPriceValueAndUnit(product, unit))
    ) {
      units = [...WEIGHT_QUANTITY_UNITS, ...units];
    }

  }

  const preferred =
    canonicalizePriceUnit(preferredUnit || "") || preferredUnit;
  if (preferred && units.includes(preferred)) {
    units = [preferred, ...units.filter((u) => u !== preferred)];
  }

  // Buying by weight: any item whose price can be expressed per pound (a
  // vendor $/lb price, or a length/piece price convertible through density
  // and geometry) can also be ordered in lb. Checkout and shipping already
  // price and weigh lb quantities. Appended after the preferred-unit
  // promotion above so the default unit (usually a length) does not change:
  // lb is offered, never imposed.
  if (
    !units.includes("lb") &&
    !shapeIsSheet(shape) &&
    !isBarWeightRemnant(product) &&
    product.price?.value &&
    convertPriceValueAndUnit(product, "lb")
  ) {
    units = [...units, "lb"];
  }

  return units;
};
